import { Navigate, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { loginWithSiteAppId, showError } from "../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
	isAuthenticated,
	isSiteAppContext,
	resolveAndApplyUserLocale,
} from "i18n";

const ProtectedRoute = ({ component: Component, children, ...rest }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [shouldNavigate, setShouldNavigate] = useState(false);
	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const { pathname, search } = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	//case for redirectedUrl with siteAppId
	const isRedirectUrl = search?.includes("siteAppId");
	const isFeedbackUrl = pathname?.includes("feedback");
	const params = new URLSearchParams(search);
	const siteAppId = params?.get("siteAppId");

	// Apply the signed-in user's effective locale whenever the active context
	// changes: their preferred language verbatim on the portal / client / admin
	// screens, or gated by the site app's enabled locales once inside one. The
	// marker distinguishes each site app AND the no-site-app (portal/admin)
	// context via a sentinel, so the locale re-resolves when crossing between
	// them — e.g. right after login lands in the portal, or moving portal↔site
	// app (SPA transitions, no reload). Site-app *switches* full-reload elsewhere
	// so the startup path covers those. Guarded per context so it resolves once;
	// resolveAndApplyUserLocale reloads only if the language actually changes.
	useEffect(() => {
		if (!isAuthenticated()) return;
		// Route-aware: management screens (portal/client/admin/profile) resolve to
		// the "__management__" context (user's preference verbatim), operational
		// site-app screens to the active site app id (gated). The stored siteAppID
		// lingers after a site-app session, so the route — not siteAppID alone —
		// decides, which is why returning to the portal re-resolves.
		const activeContext = isSiteAppContext()
			? me?.siteAppID?.toString() || siteAppId
			: "__management__";
		try {
			if (sessionStorage.getItem("localeResolvedSiteApp") === activeContext) {
				return;
			}
			sessionStorage.setItem("localeResolvedSiteApp", activeContext);
		} catch {
			// storage unavailable — resolve anyway
		}
		resolveAndApplyUserLocale({ reload: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, search]);

	useEffect(() => {
		// conditions to check if login is required
		const shouldLogin =
			!me?.position &&
			siteAppId &&
			isFeedbackUrl &&
			!isLoading &&
			!shouldNavigate;
		if (shouldLogin) {
			const handleLogin = async () => {
				setIsLoading(true);
				const response = await dispatch(loginWithSiteAppId(siteAppId, false));
				if (!response?.status) {
					setIsLoading(false);
					setShouldNavigate(false);
					dispatch(
						showError(
							response?.data ??
								"Cannot login to the site app. Please try again!"
						)
					);
					navigate("/app/portal");
					return;
				}
				setIsLoading(false);
				setShouldNavigate(true);
			};

			handleLogin();
		}
	}, [me, siteAppId, isFeedbackUrl, isLoading, shouldNavigate, dispatch]);

	useEffect(() => {
		if (shouldNavigate) {
			navigate(`${pathname}${search}`);

			setShouldNavigate(false);
		}
	}, [shouldNavigate, navigate, pathname, search]);

	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					flex: 1,
				}}
			>
				<CircularProgress size="50px" />
			</div>
		);
	}
	//me?.position to check siteApp authentication
	if ((me && !isRedirectUrl) || me?.position) return children;

	//redirect if not logged into a site
	if (!me) {
		return <Navigate to={`/login?redirectUrl=${encodeURIComponent(pathname + search)}`} />;
	}
};

export default ProtectedRoute;
